import type { MoveTypeOverrides, PlannerEmbedSettings, PlannerPrefill } from '@hopdrive/feature-move-planner';

import {
  getLockConsumerAction,
  getLockLoanerVin,
  getLockTime,
  getLockType,
  getMoveType,
  getPlanMovesSettings,
  getReadOnly,
  getShowAllButtons,
  getSkipTypeSelection,
  persistPlanMovesTypeOverrides,
} from '@utils/embedSettings';

/** Each move type's label and description query params. */
const TYPE_OVERRIDE_PARAMS: Array<[keyof MoveTypeOverrides, string, string]> = [
  ['concierge', 'conciergeLabel', 'conciergeDesc'],
  ['concierge-loaner', 'conciergeLoanerLabel', 'conciergeLoanerDesc'],
  ['one-way', 'oneWayLabel', 'oneWayDesc'],
  ['round-trip', 'roundTripLabel', 'roundTripDesc'],
];

/**
 * The integrator's move-type wording. From the query string when it names any,
 * saved beside the other plan-moves settings so navigation inside the iframe
 * keeps it; otherwise the saved wording.
 */
export function plannerTypeOverrides(searchParams: URLSearchParams): MoveTypeOverrides {
  const fromUrl: MoveTypeOverrides = {};
  for (const [type, labelParam, descParam] of TYPE_OVERRIDE_PARAMS) {
    const label = searchParams.get(labelParam);
    const description = searchParams.get(descParam);
    if (!label && !description) continue;
    fromUrl[type] = {};
    if (label) fromUrl[type]!.label = label;
    if (description) fromUrl[type]!.description = description;
  }

  if (Object.keys(fromUrl).length) {
    persistPlanMovesTypeOverrides(fromUrl);
    return fromUrl;
  }

  return (getPlanMovesSettings()?.typeOverrides as MoveTypeOverrides | undefined) ?? {};
}

/** The integrator's query-string settings, read through the embed's settings module. */
export function plannerEmbedSettings(searchParams: URLSearchParams): PlannerEmbedSettings {
  return {
    initialType: getMoveType(searchParams) || undefined,
    skipTypeSelection: getSkipTypeSelection(searchParams),
    locks: {
      type: getLockType(searchParams),
      consumerAction: getLockConsumerAction(searchParams),
      time: getLockTime(searchParams),
      loanerVin: getLockLoanerVin(searchParams),
    },
    readOnly: getReadOnly(searchParams),
    typeOverrides: plannerTypeOverrides(searchParams),
    // As in the Dealer: the picker shows when more than one workflowset's contract sells moves
    showWorkflowsetSelect: true,
    showAllButtons: getShowAllButtons(searchParams),
  };
}

/** Every query param a prefill reads; a load with none of them is not a prefill. */
export const PREFILL_PARAM_KEYS = [
  'customerFirstName', 'customerLastName', 'customerPhone', 'customerContactType', 'consumerAction',
  'pickupLandline', 'pickupEmail', 'pickupNotes', 'deliveryLandline', 'deliveryEmail', 'deliveryNotes',
  'vehicle1ReferenceId', 'vehicle1Vin', 'vehicle1Stock', 'vehicle1Make', 'vehicle1Model', 'vehicle1Year', 'vehicle1Color',
  'vehicle2ReferenceId', 'vehicle2Vin', 'vehicle2Stock', 'vehicle2Make', 'vehicle2Model', 'vehicle2Year', 'vehicle2Color',
  'additional1Contact', 'additional1Notes', 'additional2Contact', 'additional2Notes',
  'time',
] as const;

const VEHICLE_FIELDS = ['referenceId', 'vin', 'stock', 'make', 'model', 'year', 'color'] as const;

function present<T extends string>(entries: Array<[T, string | null]>): Partial<Record<T, string>> | undefined {
  const out: Partial<Record<T, string>> = {};
  for (const [key, value] of entries) if (value != null) out[key] = value;
  return Object.keys(out).length ? out : undefined;
}

/**
 * The form values an integrator passes on a direct load. `type` is the move type
 * the planner opens on: for a concierge-loaner, the consumer's direction also
 * decides which vehicle moves first.
 */
export function plannerPrefill(searchParams: URLSearchParams, type?: string): PlannerPrefill | undefined {
  if (!PREFILL_PARAM_KEYS.some(key => searchParams.get(key) != null)) return undefined;
  const get = (key: string) => searchParams.get(key);

  const fullName = [get('customerFirstName'), get('customerLastName')].filter(Boolean).join(' ').trim() || undefined;
  const phone = get('customerPhone');
  const requestedContactType = (get('customerContactType') || 'both').toLowerCase();
  const contactType = ['pickup', 'delivery', 'both'].includes(requestedContactType) ? requestedContactType : 'both';
  const rawAction = get('consumerAction');
  const action = rawAction === 'pickup' || rawAction === 'return' ? rawAction : undefined;
  const first = action && type === 'concierge-loaner' ? (action === 'pickup' ? 'loaner' : 'consumer') : undefined;

  const prefill: PlannerPrefill = {};

  if (fullName != null || phone != null || first != null || rawAction != null) {
    prefill.consumer = {
      ...(fullName != null && { name: fullName }),
      ...(phone != null && { phone }),
      ...(first != null && { first }),
      ...(action != null && { action }),
    };
  }

  const pickupLandline = get('pickupLandline');
  const pickupEmail = get('pickupEmail');
  const pickupNotes = get('pickupNotes');
  const deliveryLandline = get('deliveryLandline');
  const deliveryEmail = get('deliveryEmail');
  const deliveryNotes = get('deliveryNotes');
  if (
    [fullName, phone, pickupLandline, pickupEmail, pickupNotes, deliveryLandline, deliveryEmail, deliveryNotes].some(
      value => value != null
    )
  ) {
    const contactInfo: NonNullable<PlannerPrefill['contactInfo']> = {};
    if (contactType === 'pickup' || contactType === 'both') {
      if (fullName != null) contactInfo.pickupName = fullName;
      if (phone != null) {
        contactInfo.pickupMobile = phone;
        contactInfo.pickupPhone = phone;
      }
    }
    if (contactType === 'delivery' || contactType === 'both') {
      if (fullName != null) contactInfo.deliveryName = fullName;
      if (phone != null) {
        contactInfo.deliveryMobile = phone;
        contactInfo.deliveryPhone = phone;
      }
    }
    if (pickupLandline != null) contactInfo.pickupLandline = pickupLandline;
    if (pickupEmail != null) contactInfo.pickupEmail = pickupEmail;
    if (pickupNotes != null) contactInfo.pickupNotes = pickupNotes;
    if (deliveryLandline != null) contactInfo.deliveryLandline = deliveryLandline;
    if (deliveryEmail != null) contactInfo.deliveryEmail = deliveryEmail;
    if (deliveryNotes != null) contactInfo.deliveryNotes = deliveryNotes;
    prefill.contactInfo = contactInfo;
    if (contactType === 'both') prefill.contactPrefillSameAsPickup = true;
  }

  const vehicle1 = present(VEHICLE_FIELDS.map(field => [field, get(`vehicle1${field[0].toUpperCase()}${field.slice(1)}`)]));
  if (vehicle1) prefill.vehicle1 = vehicle1;
  const vehicle2 = present(VEHICLE_FIELDS.map(field => [field, get(`vehicle2${field[0].toUpperCase()}${field.slice(1)}`)]));
  if (vehicle2) prefill.vehicle2 = vehicle2;

  const additional1 = present<'contact' | 'notes'>([
    ['contact', get('additional1Contact')],
    ['notes', get('additional1Notes')],
  ]);
  if (additional1) prefill.additional1 = additional1;
  const additional2 = present<'contact' | 'notes'>([
    ['contact', get('additional2Contact')],
    ['notes', get('additional2Notes')],
  ]);
  if (additional2) prefill.additional2 = additional2;

  const time = get('time');
  if (time != null) prefill.time = time;

  return prefill;
}
